import { post } from 'core/utils/request';
import urlResolver from 'edrolo/utils/URLResolver';
import useSWR, { useSWRConfig } from 'swr';
import { enums as e, toast } from 'edrolo-ui';

export type useShortLinkProps = {
  id: number;
  appLabel: string;
  model: string;
  queryString?: string;
};

export const useShortLink = ({
  id,
  appLabel,
  model,
  queryString,
}: useShortLinkProps) => {
  const url = urlResolver.getPath('api:post:short-link');
  const { cache, mutate } = useSWRConfig();
  const shouldFetch = Boolean(id && appLabel && model);
  const key = shouldFetch
    ? [url, `${id}:${appLabel}:${model}`, queryString ?? '']
    : null;

  const { data, error, isLoading } = useSWR(
    key,
    async () => {
      if (!key) {
        return null;
      }

      // Check if it's already in SWR cache
      const cached = cache.get(Array.isArray(key) ? JSON.stringify(key) : key);
      if (cached) {
        return cached;
      }

      const data = {
        content_type: `${appLabel}.${model}`,
        object_id: id,
        query_string: queryString,
      };

      const response = await post(url, data);
      if (!response.share_url) {
        toast({
          icon: {
            name: e.Icons.INFO_TIP,
            size: e.Size.LARGE,
            color: e.ColorHue.RED,
          },
          title: 'Short link not available',
          description: 'Please try again later.',
        });

        throw new Error('Failed to create short link.');
      }

      // Save it into cache manually (without revalidation)
      mutate(key, response.share_url, false);
      return response.share_url;
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  return { data, error, isLoading };
};
